import { cva, type VariantProps } from "class-variance-authority";
import { CircleAlert, Info, TriangleAlert, X } from "lucide-react";
import * as React from "react";

import { Button } from "../Button";
import { twMerge } from "../tailwind-utils";

const alertVariants = cva(
  "tw-relative tw-flex tw-w-full tw-items-center tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-text-sm tw-font-normal",
  {
    variants: {
      variant: {
        info: "",
        infoAlert: "tw-border-primary",
        warning: "tw-border-orange",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

type AlertVariants = VariantProps<typeof alertVariants>;

export type AlertProps = Omit<AlertVariants, "variant"> & {
  variant: NonNullable<Required<AlertVariants>["variant"]>;
  rightElement?: React.ReactNode;
  leftElement?: React.ReactNode;
  isClosable?: boolean;
};

interface AlertComponent
  extends React.ForwardRefExoticComponent<
    React.HTMLAttributes<HTMLDivElement> &
      AlertProps &
      React.RefAttributes<HTMLDivElement>
  > {
  Title: typeof AlertTitle;
  Description: typeof AlertDescription;
}

interface IIconProps {
  variant: NonNullable<Required<AlertVariants>["variant"]>;
  className?: string;
}

// TODO: add warning variant
const IconFromVariant = ({ variant, className }: IIconProps) => {
  switch (variant) {
    case "info":
      return (
        <div
          className={twMerge(
            "tw-flex tw-w-min tw-items-center tw-justify-center",
            className,
          )}
        >
          <div className="tw-rounded-full tw-bg-primary-150 tw-p-px">
            <Info
              className="tw-h-5 tw-w-5 tw-text-primary [&>circle]:tw-stroke-none"
              size={20}
              stroke="white"
              strokeWidth={2.5}
              fill="currentColor"
            />
          </div>
        </div>
      );
    case "warning":
      return (
        <div
          className={twMerge(
            "tw-flex tw-w-min tw-items-center tw-justify-center",
            className,
          )}
        >
          <div className="tw-rounded-full tw-bg-orange-100 tw-p-px">
            <CircleAlert
              className="tw-h-5 tw-w-5 tw-text-orange [&>circle]:tw-stroke-none"
              size={20}
              stroke="white"
              strokeWidth={2.5}
              fill="currentColor"
            />
          </div>
        </div>
      );
    case "infoAlert":
      return (
        <div
          className={twMerge(
            "tw-flex tw-w-min tw-items-center tw-justify-center",
            className,
          )}
        >
          <div className="tw-rounded-full tw-bg-primary-150 tw-p-px">
            <CircleAlert
              className="tw-h-5 tw-w-5 tw-text-primary [&>circle]:tw-stroke-none"
              size={20}
              stroke="white"
              strokeWidth={2.5}
              fill="currentColor"
            />
          </div>
        </div>
      );
  }
};

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & AlertProps
>(
  (
    {
      className,
      variant = "info",
      isClosable = false,
      rightElement,
      leftElement,
      ...props
    },
    ref,
  ) => {
    const [isClosed, setIsClosed] = React.useState(false);

    return (
      <div
        ref={ref}
        role="alert"
        className={twMerge(
          alertVariants({ variant }),
          "tw-transition-all tw-duration-500 tw-ease-in-out tw-fade-in ",
          {
            "tw-opacity-0 tw-invisible tw-max-h-0": isClosed,
            "tw-opacity-100 tw-visible tw-max-h-[500px] has-[*]:tw-my-4":
              !isClosed,
          },

          className,
        )}
        {...props}
      >
        {leftElement ? leftElement : <IconFromVariant variant={variant} />}
        <div className="tw-ml-4 tw-w-full">{props.children}</div>
        {rightElement}
        {isClosable && (
          <Button
            variant="ghost"
            size="plain"
            onClick={() => setIsClosed(true)}
            className="tw-ml-2"
          >
            <X
              strokeWidth={3}
              className={twMerge("tw-h-4 tw-w-4 tw-text-gray-500", {
                "tw-text-orange": variant === "warning",
              })}
            />
          </Button>
        )}
      </div>
    );
  },
) as AlertComponent;
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h5
    ref={ref}
    className={twMerge(
      "tw-mb-1 tw-font-medium tw-leading-none tw-tracking-tight",
      className,
    )}
    {...props}
  >
    {children}
  </h5>
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={twMerge("tw-text-sm [&_p]:tw-leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

Alert.Title = AlertTitle;
Alert.Description = AlertDescription;

export { Alert, AlertTitle, AlertDescription };
